import { useEffect, useMemo, useReducer } from "react";
import { liveEval, positionEvaluationScore } from "@/features/board/review/live-eval";
import {
  createTemporaryPlayController,
  type TemporaryPlayController,
  type TemporaryPlayOrigin,
} from "./temporary-play";
import { useTemporaryMoveSound } from "./useTemporaryMoveSound";
import { createTemporaryMoveGrader } from "./temporary-grade";

const noMoves = [] as const;
export const useTemporaryPlay = (
  origin: TemporaryPlayOrigin | null,
  evaluationDepth?: number,
  moveSounds = true,
): TemporaryPlayController | null => {
  const [, render] = useReducer((value) => value + 1, 0);
  const grade = useMemo(
    () =>
      createTemporaryMoveGrader(async (fen, signal) => {
        signal.throwIfAborted();
        const result = await liveEval.analyze(
          fen,
          evaluationDepth,
          AbortSignal.any([signal, AbortSignal.timeout(20_000)]),
        );
        signal.throwIfAborted();
        return result ? { score: positionEvaluationScore(result), bestMove: result.bestMove } : null;
      }),
    [evaluationDepth],
  );
  const analyzeReply = useMemo(
    () => async (fen: string, signal: AbortSignal) => {
      signal.throwIfAborted();
      const result = await liveEval.analyze(
        fen,
        evaluationDepth,
        AbortSignal.any([signal, AbortSignal.timeout(20_000)]),
      );
      signal.throwIfAborted();
      if (!result?.bestMove) return null;
      const continuation = result.pv[0] === result.bestMove ? result.pv : [result.bestMove, ...result.pv];
      return { move: result.bestMove, continuation, depth: result.depth };
    },
    [evaluationDepth],
  );
  const controller = useMemo(
    () =>
      origin
        ? createTemporaryPlayController(
            origin,
            {
              grade,
              analyzeReply,
            },
            () => render(),
          )
        : null,
    [origin, grade, analyzeReply],
  );

  useEffect(() => () => controller?.dispose(), [controller]);
  useTemporaryMoveSound(controller?.state.moves ?? noMoves, moveSounds);
  return controller;
};
